import { supabase } from "@/lib/supabase";
import {
  useAssistantStore,
  useAuthStore,
  useFileStorageStore,
  useModelStore,
  usePromptStore,
  useThreadStore,
} from "@/store";
import { useEffect, useRef, useState } from "react";

export function useAppInitialization() {
  const [loadingStates, setLoadingStates] = useState({
    auth: false,
    assistants: false,
    threads: false,
    fileStorage: false,
    prompts: false,
    models: false,
  });

  const [hasInitiated, setHasInitiated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const initStartTime = useRef(null);
  const stepStartTimes = useRef({});
  const initComplete = useRef(false);

  const {
    initialized: authInitialized,
    initialize: initializeAuth,
    isLoading: authLoading,
    user,
    userId,
    currentWorkspaceId,
    setAssistantsInitialized,
    workspaces,
    setWorkspaces,
    setCurrentWorkspaceId,
  } = useAuthStore();

  const {
    initialized: assistantsInitialized,
    initializeAssistants,
    isLoading: assistantsLoading,
    currentAssistantId,
    assistants,
  } = useAssistantStore();

  const {
    initialized: threadsInitialized,
    initialize: initializeThreads,
    isLoading: threadsLoading,
  } = useThreadStore();

  const {
    initialized: fileStorageInitialized,
    initializeFileStorageStore,
    isLoading: fileStorageLoading,
  } = useFileStorageStore();

  const {
    initialized: modelsInitialized,
    initializeModelStore,
    isLoading: modelsLoading,
  } = useModelStore();

  const {
    initialized: promptsInitialized,
    initializePromptStore,
    isLoading: promptsLoading,
  } = usePromptStore();

  const initialize = async () => {
    if (initComplete.current || hasInitiated) {
      return { success: false, reason: "Already initialized" };
    }

    setHasInitiated(true);
    initStartTime.current = Date.now();

    try {
      setIsLoading(true);

      stepStartTimes.current.auth = Date.now();
      setLoadingStates((prev) => ({ ...prev, auth: true }));

      const authResult = await initializeAuth();
      setLoadingStates((prev) => ({ ...prev, auth: false }));

      if (!authResult?.success || !authResult?.user) {
        completeInitialization();
        return { success: false, reason: "No user data" };
      }

      const { userId, currentWorkspaceId, workspaces } =
        useAuthStore.getState();

      if (!userId) {
        completeInitialization();
        return { success: false, reason: "No userId" };
      }

      let workspaceRecoverySuccess = false;

      if (!currentWorkspaceId || !workspaces || workspaces.length === 0) {
        const { data: workspacesData, error } = await supabase
          .from("workspaces")
          .select("*")
          .eq("user_id", userId);

        if (error) {
          console.error("Error fetching workspaces:", error);
        } else {
          if (workspacesData && workspacesData.length > 0) {
            useAuthStore.getState().setWorkspaces(workspacesData);

            const personalWorkspace =
              workspacesData.find((w) => w.is_personal) || workspacesData[0];
            useAuthStore.getState().setCurrentWorkspaceId(personalWorkspace.id);

            workspaceRecoverySuccess = true;

            setTimeout(() => {
              const { currentWorkspaceId } = useAuthStore.getState();
              if (!currentWorkspaceId && personalWorkspace?.id) {
                useAuthStore
                  .getState()
                  .setCurrentWorkspaceId(personalWorkspace.id);
              }
            }, 100);
          } else {
            try {
              const { data: newWorkspace } = await supabase
                .from("workspaces")
                .insert([
                  {
                    user_id: userId,
                    owner_id: userId,
                    name: "Personal Project",
                    is_personal: true,
                    settings: {},
                    workspace_members: [userId],
                    metadata: {
                      created_at: new Date().toISOString(),
                      created_by: userId,
                    },
                  },
                ])
                .select()
                .single();

              if (newWorkspace) {
                useAuthStore.getState().setWorkspaces([newWorkspace]);
                useAuthStore.getState().setCurrentWorkspaceId(newWorkspace.id);
                workspaceRecoverySuccess = true;
              } else {
                console.error(
                  "Failed to create workspace - database returned no data"
                );
              }
            } catch (createError) {
              console.error("Error creating personal workspace:", createError);
            }
          }
        }
      } else {
        workspaceRecoverySuccess = true;
      }

      const updatedCurrentWorkspaceId =
        useAuthStore.getState().currentWorkspaceId;

      if (!assistantsInitialized && updatedCurrentWorkspaceId) {
        stepStartTimes.current.assistants = Date.now();
        setLoadingStates((prev) => ({ ...prev, assistants: true }));

        try {
          const assistantsTimeout = setTimeout(() => {
            setLoadingStates((prev) => ({ ...prev, assistants: false }));
            useAssistantStore.setState({
              initialized: true,
              initializing: false,
              error: "Initialization timed out",
            });
            useAuthStore.getState().setAssistantsInitialized(true);
          }, 15000);

          const assistantsResult = await initializeAssistants();

          clearTimeout(assistantsTimeout);

          // If no assistants were found and user just completed onboarding, create temporary assistant
          if (
            (!assistantsResult || assistantsResult.length === 0) &&
            localStorage.getItem("onboardingCompleted")
          ) {
            try {
              console.log(
                "🚀 No assistants found after onboarding, creating temporary assistant..."
              );

              // Ensure workspace is available before creating temporary assistant
              const { currentWorkspaceId } = useAuthStore.getState();
              if (!currentWorkspaceId) {
                console.log(
                  "🔄 No workspace available, skipping temporary assistant creation in initialization"
                );
                // Let the onboarding completion handle this instead
              } else {
                const { profile } = useAuthStore.getState();
                const displayName =
                  profile?.settings?.profile?.display_name ||
                  profile?.full_name ||
                  "User";

                const { createTemporaryDefaultAssistant } =
                  useAssistantStore.getState();
                await createTemporaryDefaultAssistant(displayName);
                console.log("✅ Temporary assistant created as fallback");
              }
            } catch (tempError) {
              console.error(
                "❌ Error creating fallback temporary assistant:",
                tempError
              );
            }
          }

          if (assistantsResult?.success) {
            useAuthStore.getState().setAssistantsInitialized(true);
          } else {
            useAuthStore.getState().setAssistantsInitialized(true);
          }

          // Clean up orphaned "Untitled Assistant" entries from interrupted inline creations
          useAssistantStore.getState().cleanupOrphanedAssistants();
        } catch (assistantError) {
          console.error("Error initializing assistants:", assistantError);

          // If assistant initialization fails and user just completed onboarding, create temporary assistant
          if (localStorage.getItem("onboardingCompleted")) {
            try {
              console.log(
                "🚀 Assistant initialization failed after onboarding, creating temporary assistant..."
              );

              // Ensure workspace is available before creating temporary assistant
              const { currentWorkspaceId } = useAuthStore.getState();
              if (!currentWorkspaceId) {
                console.log(
                  "🔄 No workspace available, skipping emergency temporary assistant creation in initialization"
                );
                // Let the onboarding completion handle this instead
              } else {
                const { profile } = useAuthStore.getState();
                const displayName =
                  profile?.settings?.profile?.display_name ||
                  profile?.full_name ||
                  "User";

                const { createTemporaryDefaultAssistant } =
                  useAssistantStore.getState();
                await createTemporaryDefaultAssistant(displayName);
                console.log(
                  "✅ Temporary assistant created as emergency fallback"
                );
              }
            } catch (tempError) {
              console.error(
                "❌ Error creating emergency temporary assistant:",
                tempError
              );
            }
          }

          useAssistantStore.setState({
            initialized: true,
            initializing: false,
            error: assistantError.message || "Failed to initialize",
          });
          useAuthStore.getState().setAssistantsInitialized(true);
        }

        setLoadingStates((prev) => ({ ...prev, assistants: false }));
      } else if (!updatedCurrentWorkspaceId) {
        useAuthStore.getState().setAssistantsInitialized(true);
      }

      if (!threadsInitialized && updatedCurrentWorkspaceId) {
        stepStartTimes.current.threads = Date.now();
        setLoadingStates((prev) => ({ ...prev, threads: true }));

        try {
          const threadsResult = await initializeThreads();
        } catch (threadsError) {
          console.error("Error initializing threads:", threadsError);
        } finally {
          setLoadingStates((prev) => ({ ...prev, threads: false }));
        }
      }

      if (!fileStorageInitialized) {
        stepStartTimes.current.fileStorage = Date.now();
        setLoadingStates((prev) => ({ ...prev, fileStorage: true }));

        try {
          const result = await initializeFileStorageStore(userId);
        } catch (fileStorageError) {
          console.error("Error initializing file storage:", fileStorageError);
          useFileStorageStore.setState({
            initialized: true,
            error:
              fileStorageError?.message || "Failed to initialize file storage",
          });
        }

        setLoadingStates((prev) => ({ ...prev, fileStorage: false }));
      }

      if (!threadsInitialized && !stepStartTimes.current.threads) {
        stepStartTimes.current.threads = Date.now();
        setLoadingStates((prev) => ({ ...prev, threads: true }));

        try {
          const threadsTimeout = setTimeout(() => {
            setLoadingStates((prev) => ({ ...prev, threads: false }));
            useThreadStore.setState({
              initialized: true,
              isLoading: false,
              error: "Initialization timed out",
            });
          }, 15000);

          await initializeThreads().catch((threadsError) => {
            console.error("Error initializing threads:", threadsError);
            useThreadStore.setState({
              initialized: true,
              isLoading: false,
              error: threadsError.message || "Failed to initialize threads",
            });
          });

          clearTimeout(threadsTimeout);

          const { threads } = useThreadStore.getState();
        } catch (threadsError) {
          console.error("Error initializing threads:", threadsError);
          useThreadStore.setState({
            initialized: true,
            isLoading: false,
            error: threadsError.message || "Failed to initialize threads",
          });
        }

        setLoadingStates((prev) => ({ ...prev, threads: false }));
      }

      if (!modelsInitialized) {
        stepStartTimes.current.models = Date.now();
        setLoadingStates((prev) => ({ ...prev, models: true }));

        try {
          await initializeModelStore();
        } catch (modelsError) {
          console.error("Error initializing models:", modelsError);
        }

        setLoadingStates((prev) => ({ ...prev, models: false }));
      }

      if (!promptsInitialized) {
        stepStartTimes.current.prompts = Date.now();
        setLoadingStates((prev) => ({ ...prev, prompts: true }));

        try {
          await initializePromptStore(updatedCurrentWorkspaceId);
        } catch (promptsError) {
          console.error("Error initializing prompts:", promptsError);
        }

        setLoadingStates((prev) => ({ ...prev, prompts: false }));
      }

      completeInitialization();
      return { success: true, reason: "Initialization completed successfully" };
    } catch (error) {
      console.error("Error during app initialization:", error);
      setError(error);
      completeInitialization();
      return { success: false, reason: error?.message || "An error occurred" };
    }
  };

  const completeInitialization = () => {
    if (initComplete.current) return;

    const duration = Date.now() - (initStartTime.current || Date.now());

    if (userId) {
      fetchWorkspacesFromUserWorkspacesArray(userId);
    }

    initComplete.current = true;
    setIsInitialized(true);
    setIsLoading(false);
  };

  const fetchWorkspacesFromUserWorkspacesArray = async (userId) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("user_workspaces")
        .eq("id", userId)
        .single();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return;
      }

      if (!profile?.user_workspaces || profile.user_workspaces.length === 0) {
        return;
      }

      const currentWorkspaces = useAuthStore.getState().workspaces || [];
      const currentWorkspaceIds = currentWorkspaces.map((w) => w.id);

      const missingWorkspaceIds = profile.user_workspaces.filter(
        (id) => !currentWorkspaceIds.includes(id)
      );

      if (missingWorkspaceIds.length === 0) {
        return;
      }

      const { data: missingWorkspaces, error: workspacesError } = await supabase
        .from("workspaces")
        .select("*")
        .in("id", missingWorkspaceIds);

      if (workspacesError) {
        console.error("Error fetching missing workspaces:", workspacesError);
        return;
      }

      if (!missingWorkspaces || missingWorkspaces.length === 0) {
        return;
      }

      const updatedWorkspaces = [...currentWorkspaces, ...missingWorkspaces];

      useAuthStore.getState().setWorkspaces(updatedWorkspaces);

      const currentWorkspaceId = useAuthStore.getState().currentWorkspaceId;
      if (!currentWorkspaceId) {
        const personalWorkspace =
          updatedWorkspaces.find((w) => w.is_personal) || updatedWorkspaces[0];
        if (personalWorkspace) {
          useAuthStore.getState().setCurrentWorkspaceId(personalWorkspace.id);
        }
      }
    } catch (error) {
      console.error("Error in fetchWorkspacesFromUserWorkspacesArray:", error);
    }
  };

  useEffect(() => {
    try {
      const redirectCompleted = localStorage.getItem("auth_redirect_completed");
      const redirectTimestamp = parseInt(redirectCompleted || "0");
      const isRecentRedirect = Date.now() - redirectTimestamp < 10000;

      if (isRecentRedirect) {
        if (!hasInitiated || !isLoading) {
          sessionStorage.removeItem("auth_init_cache");
          sessionStorage.removeItem("auth_init_timestamp");
          sessionStorage.removeItem("auth_init_error");
          setHasInitiated(false);
          setIsLoading(true);
          setTimeout(() => {
            initialize();
            localStorage.removeItem("auth_redirect_completed");
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error handling post-OAuth state:", error);
    }
  }, [authInitialized, userId, isInitialized, hasInitiated, isLoading]);

  useEffect(() => {
    if (
      authInitialized &&
      userId &&
      !hasInitiated &&
      !isInitialized &&
      !isLoading
    ) {
      setHasInitiated(false);
      setTimeout(initialize, 50);
    }
  }, [authInitialized, userId, hasInitiated, isInitialized, isLoading]);

  useEffect(() => {
    if (!hasInitiated && authInitialized) {
      initialize();
    }
  }, [hasInitiated, authInitialized, userId]);

  useEffect(() => {
    if (authInitialized && !isInitialized && hasInitiated) {
      const stalledInitTimer = setTimeout(() => {
        setHasInitiated(false);
        setTimeout(() => {
          if (!isInitialized) {
            initialize();
          }
        }, 500);
      }, 8000);

      return () => clearTimeout(stalledInitTimer);
    }
  }, [authInitialized, isInitialized, hasInitiated]);

  useEffect(() => {
    if (hasInitiated && !isInitialized) {
      const timeoutId = setTimeout(() => {
        if (!isInitialized) {
          completeInitialization();
        }
      }, 30000);

      return () => clearTimeout(timeoutId);
    }
  }, [hasInitiated, isInitialized]);

  useEffect(() => {}, [
    isInitialized,
    isLoading,
    authInitialized,
    assistantsInitialized,
    threadsInitialized,
    fileStorageInitialized,
    promptsInitialized,
    modelsInitialized,
  ]);

  useEffect(() => {
    try {
      const forceInitFlag = localStorage.getItem(
        "force_initialize_after_oauth"
      );

      if (forceInitFlag === "true") {
        setHasInitiated(false);
        setIsInitialized(false);
        setIsLoading(false);
        setError(null);

        localStorage.removeItem("force_initialize_after_oauth");

        setTimeout(() => {
          if (!isInitialized) {
            initialize();
          }
        }, 200);
      }
    } catch (err) {
      console.error("Error checking force initialization flag:", err);
    }
  }, []);

  useEffect(() => {
    if (authInitialized && userId && !hasInitiated && !isInitialized) {
      // Auth initialized, user id, has initiated, is initialized
    }
  }, [authInitialized, userId, hasInitiated, isInitialized, isLoading]);

  useEffect(() => {
    const isOAuthPath =
      typeof window !== "undefined" &&
      window.location.pathname === "/chat" &&
      document.referrer.includes("/auth/callback");

    if (
      isOAuthPath &&
      authInitialized &&
      userId &&
      !isInitialized &&
      !hasInitiated
    ) {
      setTimeout(initialize, 50);
    }
  }, [
    window?.location?.pathname,
    authInitialized,
    userId,
    isInitialized,
    hasInitiated,
  ]);

  useEffect(() => {
    let recoveryTimer = null;

    if (authInitialized && userId && !hasInitiated && !isLoading) {
      recoveryTimer = setTimeout(() => {
        setHasInitiated(false);
        setIsInitialized(false);
        setTimeout(initialize, 50);
      }, 3000);
    }

    return () => {
      if (recoveryTimer) clearTimeout(recoveryTimer);
    };
  }, [authInitialized, userId, hasInitiated, isLoading]);

  return {
    isLoading,
    isInitialized,
    loadingStates,
    storeStates: {
      authInitialized,
      authLoading,
      assistantsInitialized,
      assistantsLoading,
      threadsInitialized,
      threadsLoading,
      fileStorageInitialized,
      fileStorageLoading,
      promptsInitialized,
      promptsLoading,
      modelsInitialized,
      modelsLoading,
    },
    error,
    hasInitiated,
    setIsInitialized,
    setIsLoading,
    completeInitialization,
  };
}

export default useAppInitialization;
